import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { LayoutGrid, Plus } from 'lucide-react'
import { useState } from 'react'
import { v4 as uuidv4 } from 'uuid'

import { Button } from '@/components/ui/button'
import { MediaGalleryModal } from '@/components/media/MediaGalleryModal'

import { OfferModel } from '@/types'
import { BLOCK_CATALOG, buildBlockDefaults, getBlockDef } from '@/lib/campaign-blocks/catalog'

import { BlockCard } from './BlockCard'

interface Props {
  offerType: OfferModel.OfferType
  blocks: OfferModel.CampaignBlock[]
  onChange: (blocks: OfferModel.CampaignBlock[]) => void
}

// Sets a value at a dot-path (supporting array indices) inside an object,
// immutably — used to write a picked image URL into a block's nested props.
function setDeep(
  obj: Record<string, unknown> | unknown[],
  parts: string[],
  val: unknown,
): Record<string, unknown> | unknown[] {
  const [head, ...tail] = parts
  if (Array.isArray(obj)) {
    const idx = Number(head)
    const arr = [...obj]
    arr[idx] = tail.length === 0 ? val : setDeep((arr[idx] as Record<string, unknown>) ?? {}, tail, val)
    return arr
  }
  if (tail.length === 0) return { ...obj, [head]: val }
  const child = obj[head]
  return {
    ...obj,
    [head]: setDeep(Array.isArray(child) ? child : ((child as Record<string, unknown>) ?? {}), tail, val),
  }
}

export const CampaignPageBuilder = ({ offerType, blocks, onChange }: Props) => {
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [mediaTarget, setMediaTarget] = useState<{ blockId: string; path: string } | null>(null)

  const palette = BLOCK_CATALOG[offerType] ?? []

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const addBlock = (type: string) => {
    const def = getBlockDef(offerType, type)
    if (!def) return
    const block: OfferModel.CampaignBlock = {
      id: uuidv4(),
      type,
      variant: def.variants[0]?.id,
      props: buildBlockDefaults(def),
    }
    onChange([...blocks, block])
    setExpandedId(block.id)
  }

  const removeBlock = (id: string) => {
    onChange(blocks.filter((b) => b.id !== id))
    if (expandedId === id) setExpandedId(null)
  }

  const updateBlockVariant = (id: string, variant: string) => {
    onChange(blocks.map((b) => (b.id === id ? { ...b, variant } : b)))
  }

  const updateBlockProps = (id: string, props: Record<string, unknown>) => {
    onChange(blocks.map((b) => (b.id === id ? { ...b, props } : b)))
  }

  const updateBlockStyles = (id: string, styles: Record<string, unknown>) => {
    onChange(blocks.map((b) => (b.id === id ? { ...b, styles } : b)))
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = blocks.findIndex((b) => b.id === active.id)
    const newIndex = blocks.findIndex((b) => b.id === over.id)
    if (oldIndex === -1 || newIndex === -1) return
    onChange(arrayMove(blocks, oldIndex, newIndex))
  }

  const handleImageSelected = (url: string) => {
    if (!mediaTarget) return
    const block = blocks.find((b) => b.id === mediaTarget.blockId)
    if (block) {
      const nextProps = setDeep(block.props, mediaTarget.path.split('.'), url) as Record<string, unknown>
      updateBlockProps(block.id, nextProps)
    }
    setMediaTarget(null)
  }

  return (
    <div className="space-y-4">
      {/* Palette */}
      <div className="rounded-lg bg-subdued p-3">
        <p className="mb-2 flex items-center gap-2 text-sm font-medium text-ink">
          <LayoutGrid className="size-4 text-text-2" aria-hidden /> Add a block
        </p>
        <div className="flex flex-wrap gap-2">
          {palette.map((def) => (
            <Button
              key={def.type}
              variant="outline"
              onClick={() => addBlock(def.type)}
            >
              <Plus />
              {def.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Canvas */}
      {blocks.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border py-12 text-center text-sm text-muted-foreground">
          No blocks yet. Add a block above to start building the campaign page.
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={blocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-2">
              {blocks.map((block) => {
                const def = getBlockDef(offerType, block.type)
                if (!def) return null
                return (
                  <BlockCard
                    key={block.id}
                    block={block}
                    def={def}
                    expanded={expandedId === block.id}
                    onToggle={() => setExpandedId(expandedId === block.id ? null : block.id)}
                    onRemove={() => removeBlock(block.id)}
                    onVariantChange={(variant) => updateBlockVariant(block.id, variant)}
                    onPropChange={(key, value) =>
                      updateBlockProps(block.id, { ...block.props, [key]: value })
                    }
                    onStyleChange={(key, value) =>
                      updateBlockStyles(block.id, { ...block.styles, [key]: value })
                    }
                    onImageRequest={(path) => setMediaTarget({ blockId: block.id, path })}
                  />
                )
              })}
            </div>
          </SortableContext>
        </DndContext>
      )}

      <MediaGalleryModal
        open={mediaTarget !== null}
        onOpenChange={(open) => {
          if (!open) setMediaTarget(null)
        }}
        multiple={false}
        onSelect={(files) => {
          if (files[0]?.url) handleImageSelected(files[0].url)
        }}
      />
    </div>
  )
}
